import * as React from 'react';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import {Body} from 'shells/typography/body';

import type {
    GetAssignments_meeting_assignments_edges as Assignment,
    GetAssignments_meeting_assignments_edges_node_assigneeEntity as AssigneeEntity,
} from 'nutshell-graphql-types';

import {AssignmentTable} from './assignment-table';
import {AssignmentTableEmptyState} from './assignment-table-empty-state';
import {UnassignActionMenu} from './unassign-action-menu';
import {UsersTeamsSelectionModal} from './users-teams-selection-modal';

type Props = {
    teamAssignments: Assignment[];
    isLoading: boolean;
    onAssignmentChange: (assignments: string[]) => void;
    onResetSeveredMeeting: (severedMeetingId: string) => void;
};

export function TeamsTable(props: Props) {
    const [isOpen, setIsOpen] = React.useState(false);

    const firstTeam = props.teamAssignments[0];
    const selectedTeams = props.teamAssignments.map((team) => team.node.assigneeEntity);

    // @ts-expect-error
    const handleUnassign = (teamToUnassign) => {
        const newSelectedTeams = selectedTeams.filter((team) => team.id !== teamToUnassign.id);
        props.onAssignmentChange(newSelectedTeams.map((team) => team.id));
    };

    return (
        <>
            <LegacySetupTableWrapper title='Teams'>
                <StackLegacy spacing={16}>
                    <LegacySetupTableContainer>
                        <AssignmentTable
                            assignedMeetings={firstTeam ? firstTeam.meetings : []}
                            firstRow={
                                <AddButtonRow
                                    buttonText='Assign a team'
                                    onClick={() => setIsOpen(true)}
                                />
                            }
                            secondRow={
                                firstTeam && (
                                    <TeamRow
                                        team={firstTeam.node.assigneeEntity}
                                        onUnassign={handleUnassign}
                                    />
                                )
                            }
                            specialStates={[
                                {
                                    shouldRender: () => props.isLoading,
                                    component: <LoadingPage />,
                                },
                                {
                                    shouldRender: () => !firstTeam,
                                    component: (
                                        <AssignmentTableEmptyState
                                            title='No teams assigned'
                                            description='Assign a team to this meeting so they can start booking leads'
                                        />
                                    ),
                                },
                            ]}
                            onResetSeveredMeeting={props.onResetSeveredMeeting}
                        />
                    </LegacySetupTableContainer>
                    {props.teamAssignments.slice(1).map((team) => (
                        <LegacySetupTableContainer key={team.node.id} wrapsEntireTable={true}>
                            <AssignmentTable
                                key={team.node.id}
                                assignedMeetings={team.meetings}
                                firstRow={
                                    <TeamRow
                                        team={team.node.assigneeEntity}
                                        onUnassign={handleUnassign}
                                    />
                                }
                                onResetSeveredMeeting={props.onResetSeveredMeeting}
                            />
                        </LegacySetupTableContainer>
                    ))}
                </StackLegacy>
            </LegacySetupTableWrapper>
            {isOpen && (
                <UsersTeamsSelectionModal
                    isOpen={true}
                    // @ts-expect-error
                    selected={selectedTeams}
                    type='teams'
                    onSelection={({selectedTeams: newSelectedTeams}) => {
                        props.onAssignmentChange(newSelectedTeams);
                        setIsOpen(false);
                    }}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </>
    );
}

type TeamRowProps = {
    team: AssigneeEntity;
    onUnassign: (team: AssigneeEntity) => void;
};

function TeamRow(props: TeamRowProps) {
    return (
        <div className='pad-8 border-bottom flex justify-sb'>
            <Body size='small' isBold={true} color='dark'>
                {/* @ts-expect-error */}
                {props.team.name} ({props.team.numUsers})
            </Body>
            <UnassignActionMenu
                assigneeToUnassign={props.team.name}
                onConfirm={() => {
                    props.onUnassign(props.team);
                }}
            />
        </div>
    );
}
